import { DocumentAttachmentChangesContext } from '@/context/DocumentAttachmentChangesContext';
import { useDocumentAttachmentChanges } from '@pages/document/useDocumentAttachmentChanges';
import { DocumentRuntimeContext, isDocumentRuntimeReadOnly } from '@/context/DocumentRuntimeContext';
import { createContext, FC, lazy, memo, ReactNode, Suspense, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { IDocumentData } from '@models/document/IDocumentData';
import { IDocumentScheme, TableTab } from '@models/document/IDocumentScheme';
import { IFieldElem } from '@models/IFormData';
import { UseFormReturn } from 'react-hook-form';
import DocumentTabItem, { IDocumentTabItem } from './tabs/DocumentTabItem/DocumentTabItem';
import './DocumentTabBuilder.scss';
import { ITab } from '@srm/ui/types/ITab';
import type { IDocumentTab } from './tabs/DocumentTab.types';
import DocumentTabTenderStatus from './tabs/DocumentTabTenderStatus/DocumentTabTenderStatus';
import Tabs from '@molecules/Tabs';
import Preloader from '@srm/ui/preloader';
import DocumentSkeleton from '@organisms/document/DocumentSkeleton';
import { IDocumentStatusBar } from '@models/document/IDocumentStatusBar';
import { IDocumentRouteMap } from '@models/document/IDocumentRouteMap';
import DocumentTabCopyAttach from './tabs/DocumentTabAttach/DocumentTabCopyAttach';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';
import { useDocumentVisibleTabs } from './useDocumentVisibleTabs';
import { DOCUMENT_TABS_SETTLING_CLASS } from '../DocumentWorkspace/documentModeTransition';
import { ConfigService } from '@/configuration/services/configService';
import { buildTabFieldIndex, isDocumentResolverValidationEnabled } from '@utils/validation/document';
import { resolveFirstInvalidTabRequest } from './useFirstInvalidTabRequest';
import { scrollFirstDocumentFieldIntoView } from './documentFieldNavigation';
import { clsx } from 'clsx';
import type { ICompletionNavigationRequest } from '../DocumentCompletionNavigator/useDocumentCompletion';

// Тяжёлые вкладки (DevExtreme-гриды связей/вложений/маршрутов/ЕИС и лента
// согласования) грузим лениво отдельными чанками. Tabs монтирует их контент при
// первом открытии и сохраняет посещённые панели, не пересоздавая гриды при возврате.
const DocumentTabLinks = lazy(() => import('./tabs/DocumentTabLinks/DocumentTabLinks'));
const DocumentTabAttach = lazy(() => import('./tabs/DocumentTabAttach/DocumentTabAttach'));
const DocumentTabRoutes = lazy(() => import('./tabs/DocumentTabRoutes/DocumentTabRoutes'));
const DocumentTabRoutesMap = lazy(() => import('./tabs/DocumentTabRoutesMap/DocumentTabRoutesMap'));
const DocumentTabRouteApprove = lazy(() => import('./tabs/DocumentTabRouteApprove'));
const DocumentTabLoadEisLog = lazy(() => import('./tabs/DocumentTabLoadEisLog/DocumentTabLoadEisLog'));

// Suspense-обёртка для лениво-подгружаемых вкладок: спиннер виден только в
// области панели на время загрузки чанка, заголовки вкладок при этом не мигают.
const withTabSuspense = (node: ReactNode): ReactNode => (
    <Suspense fallback={<Preloader size="lg" position="default" />}>{node}</Suspense>
);

// Режим правки — контекстом, а не пропом в описании вкладок: isEdit в deps useMemo пересоздавал
// весь массив вкладок на каждом view↔edit — каскадный перерендер всех смонтированных панелей
// (тысячи DOM-узлов) и полный пересчёт видимости вкладок (tab-формулы, GETLINKSCOUNT). Контекст
// перерендеривает только сами DocumentTabItem, identity описаний вкладок стабильна.
// Именно проп билдера (не store): в support-preview isEdit билдера отличается от document.isEdit.
const DocumentTabEditModeContext = createContext(false);

const ModeAwareDocumentTabItem: FC<Omit<IDocumentTabItem, 'isEdit'>> = (props) => (
    <DocumentTabItem {...props} isEdit={useContext(DocumentTabEditModeContext)} />
);

export interface IDocumentTabBuilderProps {
    scheme: IDocumentScheme;
    statusBar?: IDocumentStatusBar;
    routeMap?: IDocumentRouteMap;
    isNew: boolean;
    isEdit: boolean;
    turnOffInvokeChangesOnNew: boolean;
    methods: UseFormReturn<IDocumentData>;
    setError: (errors?: string[]) => void;
    fields: Record<string, IFieldElem>;
    docId: string;
    useLinks?: boolean;
    hideRoutes?: boolean;
    hideStatusBar?: boolean;
    hideRoutesMap?: boolean;
    hideRouteApprove?: boolean;
    hideEisLog?: boolean;
    hideAttachments?: boolean;
    hideLinks?: boolean;
    baseDocId: string;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
    /** Запрос на переход к первому видимому табу с RHF-ошибкой. */
    errorTabRequest?: { nonce: number; errorIds: number[]; documentTabIds?: Record<number, string> };
    /** Подтверждает выполнение одноразового запроса навигации. */
    onErrorTabRequestHandled?: (nonce: number) => void;
    navigationRequest?: ICompletionNavigationRequest;
    onNavigationReady?: (request: ICompletionNavigationRequest, panel: HTMLElement) => void;
    /** Целевая вкладка навигации не среди видимых — немедленный отказ вместо таймаута. */
    onNavigationUnreachable?: (nonce: number) => void;
}

const DocumentTabBuilder: FC<IDocumentTabBuilderProps> = ({
    scheme,
    statusBar,
    routeMap,
    isNew,
    isEdit,
    turnOffInvokeChangesOnNew,
    methods,
    setError,
    fields,
    docId,
    useLinks = true,
    hideRoutes = false,
    hideStatusBar = false,
    hideRoutesMap = false,
    hideRouteApprove = false,
    hideEisLog = false,
    hideAttachments = false,
    hideLinks = false,
    baseDocId,
    ensureExternalDataSourceLoaded,
    errorTabRequest,
    onErrorTabRequestHandled,
    navigationRequest,
    onNavigationReady,
    onNavigationUnreachable,
}: IDocumentTabBuilderProps) => {
    const useResolverValidation = isDocumentResolverValidationEnabled(ConfigService.get().application);
    // Якоря полей свежесмонтированной вкладки появляются ПОЗЖЕ ready-коммита Tabs
    // (useFormulaState видимости стартует с false и раскрывает поле после эффекта/формулы),
    // поэтому промах scrollFirstDocumentFieldIntoView ретраится по rAF до дедлайна.
    const scrollRetryRef = useRef<number | null>(null);
    const cancelScrollRetry = useCallback(() => {
        if (scrollRetryRef.current !== null) {
            cancelAnimationFrame(scrollRetryRef.current);
            scrollRetryRef.current = null;
        }
    }, []);
    useEffect(() => cancelScrollRetry, [cancelScrollRetry]);

    const createLinkTab = useCallback(
        (element: TableTab, _tabs: ITab[]) => {
            const includedFields: number[] = [];
            if (!element.key) return;
            const tab: IDocumentTab = {
                value: `links-${element.key}`,
                label: element.name,
                tab: withTabSuspense(
                    <DocumentTabLinks
                        key={element.key}
                        tables={[element]}
                        docId={docId}
                        fields={fields}
                        formMethods={methods}
                        isNew={isNew}
                        isEdit={false}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />,
                ),
                url: useLinks ? `#links-${element.key}` : undefined,
                exact: true,
                includedFields: includedFields,
                formMethods: methods,
                fields,
                visibilityRules: element.visibilityRules,
                ensureExternalDataSourceLoaded,
            };
            _tabs.push(tab);
        },
        [docId, ensureExternalDataSourceLoaded, fields, isNew, methods, useLinks],
    );

    const createAttachTab = useCallback(
        (element: TableTab, _tabs: ITab[]) => {
            const includedFields: number[] = [];
            if (!element.key) return;
            const tab: IDocumentTab = {
                value: `attachments-${element.key}`,
                label: element.name,
                tab: withTabSuspense(
                    <DocumentTabAttach
                        key={element.key}
                        tables={[element]}
                        docId={docId}
                        fields={fields}
                        formMethods={methods}
                        isNew={isNew}
                        isEdit={false}
                        ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                    />,
                ),
                url: useLinks ? `#attachments-${element.key}` : undefined,
                exact: true,
                includedFields: includedFields,
                formMethods: methods,
                fields,
                visibilityRules: element.visibilityRules,
                ensureExternalDataSourceLoaded,
            };
            _tabs.push(tab);
        },
        [docId, ensureExternalDataSourceLoaded, fields, isNew, methods, useLinks],
    );

    // Набор вкладок — чистая производная схемы и режима, поэтому считается на рендере.
    // Через useState + useEffect он отставал на коммит: смена режима сперва перерисовывала
    // форму со СТАРЫМИ (замороженными в state) элементами вкладок и только потом с новыми.
    const tabs = useMemo<ITab[] | undefined>(() => {
        const _tabs: ITab[] = [];
        if (scheme && scheme.form && scheme.form.view) {
            // За флагом resolver-валидации: статический tab→indices из схемы вместо
            // рантайм-мутации includedFields (нужно для ленивого рендера табов в Фазе 4).
            const tabFieldIndex = useResolverValidation && fields ? buildTabFieldIndex(scheme, fields) : undefined;
            scheme.form.view.rows.row.forEach((element) => {
                const blockId = element.columns[0].blocks[0].id;
                const includedFields: number[] = tabFieldIndex ? Array.from(tabFieldIndex.get(blockId) ?? []) : [];
                const tab: IDocumentTab = {
                    value: `page-${blockId}`,
                    label: element.columns[0].blocks[0].name,
                    tab: (
                        <ModeAwareDocumentTabItem
                            formMethods={methods}
                            docId={docId}
                            setError={setError}
                            fields={fields}
                            key={element.columns[0].blocks[0].id}
                            tab={element.columns[0].blocks[0]}
                            includedFields={includedFields}
                            scheme={scheme}
                            isNew={isNew}
                            turnOffInvokeChangesOnNew={turnOffInvokeChangesOnNew}
                            ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                        />
                    ),
                    url: useLinks ? `#page-${element.columns[0].blocks[0].id}` : undefined,
                    exact: true,
                    includedFields: includedFields,
                    formMethods: methods,
                    fields,
                    visibilityRules: element.columns[0].blocks[0].visibilityRules,
                    nameFormula: element.columns[0].blocks[0].nameFormula,
                    evalNameFormula: element.columns[0].blocks[0].evalNameFormula,
                    ensureExternalDataSourceLoaded,
                    keepMounted: !useResolverValidation,
                };
                _tabs.push(tab);
            });
            if (!isNew) {
                if (!hideAttachments && !scheme.attach.singlePage) {
                    scheme.attach.tables.forEach((element) => {
                        createAttachTab(element, _tabs);
                    });
                } else if (!hideAttachments) {
                    // обрабатываем таблицы на отдельной странице
                    scheme.attach.tables
                        .filter((el) => el.separatePage)
                        .forEach((element) => {
                            createAttachTab(element, _tabs);
                        });

                    const includedFields: number[] = [];
                    const tab: IDocumentTab = {
                        value: 'attachments',
                        label: 'Документация',
                        tab: withTabSuspense(
                            <DocumentTabAttach
                                tables={scheme.attach.tables.filter((el) => !el.separatePage)}
                                docId={docId}
                                fields={fields}
                                formMethods={methods}
                                isNew={isNew}
                                isEdit={false}
                                ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                            />,
                        ),
                        url: useLinks ? `#attachments` : undefined,
                        exact: true,
                        includedFields: includedFields,
                        formMethods: methods,
                        fields,
                        visibilityRules: scheme.attach.visibilityRules,
                        ensureExternalDataSourceLoaded,
                    };
                    _tabs.push(tab);
                }

                if (!hideLinks && !scheme.links.singlePage) {
                    scheme.links.tables.forEach((element) => {
                        createLinkTab(element, _tabs);
                    });
                } else if (!hideLinks) {
                    // обрабатываем таблицы на отдельной странице
                    scheme.links.tables
                        .filter((el) => el.separatePage)
                        .forEach((element) => {
                            createLinkTab(element, _tabs);
                        });

                    const includedFields: number[] = [];
                    const tab: IDocumentTab = {
                        value: 'links',
                        label: 'Связанные документы',
                        tab: withTabSuspense(
                            <DocumentTabLinks
                                tables={scheme.links.tables.filter((el) => !el.separatePage)}
                                docId={docId}
                                fields={fields}
                                formMethods={methods}
                                isNew={isNew}
                                isEdit={false}
                                ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                            />,
                        ),
                        url: useLinks ? `#links` : undefined,
                        exact: true,
                        includedFields: includedFields,
                        formMethods: methods,
                        fields,
                        visibilityRules: scheme.links.visibilityRules,
                        ensureExternalDataSourceLoaded,
                    };
                    _tabs.push(tab);
                }

                if (!hideRoutes && scheme.visibleRoutes) {
                    _tabs.push({
                        value: 'routes',
                        label: 'Маршруты',
                        tab: withTabSuspense(<DocumentTabRoutes docId={docId} />),
                        url: useLinks ? '#routes' : undefined,
                        exact: true,
                    });
                }
                if (!hideStatusBar && scheme.visibleStatusBar) {
                    _tabs.push({
                        value: 'status',
                        label: scheme.statusBarTabName,
                        tab: <DocumentTabTenderStatus statusBar={statusBar} />,
                        url: useLinks ? '#status' : undefined,
                        exact: true,
                    });
                }
                if (!hideRoutesMap && scheme.visibleRoutesMap) {
                    _tabs.push({
                        value: 'routesmap',
                        label: 'Карта маршрута',
                        tab: withTabSuspense(
                            <DocumentTabRoutesMap docId={docId} routeMap={routeMap} fields={fields} />,
                        ),
                        url: useLinks ? '#routesmap' : undefined,
                        exact: true,
                    });
                }
                if (!hideRouteApprove && scheme.visibleRouteApprove) {
                    _tabs.push({
                        value: 'routeapproval',
                        label: 'Ход согласования',
                        tab: withTabSuspense(
                            <DocumentTabRouteApprove docId={docId} routeMap={routeMap} fields={fields} />,
                        ),
                        url: useLinks ? '#routeapproval' : undefined,
                        exact: true,
                    });
                }
                if (!hideEisLog && scheme.visibleLoadEisLog) {
                    _tabs.push({
                        value: 'eishistory',
                        label: 'История отправки ЕИС',
                        tab: withTabSuspense(<DocumentTabLoadEisLog docId={docId} />),
                        url: useLinks ? '#eishistory' : undefined,
                        exact: true,
                    });
                }
            } else {
                if (scheme.baseActionsSettings.attachesCopyAndEdit.visible && baseDocId) {
                    _tabs.push({
                        value: 'copyattachments',
                        label: 'Переносимая документация',
                        tab: <DocumentTabCopyAttach baseDocId={baseDocId} />,
                        url: `#copyattachments`,
                        exact: false,
                    });
                }
            }

            return _tabs;
        }

        return undefined;
    }, [
        baseDocId,
        createAttachTab,
        createLinkTab,
        docId,
        ensureExternalDataSourceLoaded,
        fields,
        hideAttachments,
        hideEisLog,
        hideLinks,
        hideRouteApprove,
        hideRoutes,
        hideRoutesMap,
        hideStatusBar,
        isNew,
        methods,
        routeMap,
        scheme,
        setError,
        statusBar,
        turnOffInvokeChangesOnNew,
        useLinks,
        useResolverValidation,
    ]);

    const { tabs: visibleTabs, settled: tabsSettled } = useDocumentVisibleTabs({
        tabs,
        methods,
        isEdit,
        isNew,
        ensureExternalDataSourceLoaded,
    });
    const runtime = useContext(DocumentRuntimeContext);
    const attachmentVisibilityTabs = useMemo(() => (scheme.attach?.tables ?? []).map((table) => ({
        value: table.key, label: table.name, visibilityRules: table.visibilityRules,
        formMethods: methods, fields, includedFields: [],
    })), [scheme.attach, methods, fields]);
    const { tabs: visibleAttachmentTables, settled: attachmentsSettled } = useDocumentVisibleTabs({
        tabs: attachmentVisibilityTabs, methods, isEdit, isNew, ensureExternalDataSourceLoaded });
    const attachmentLocations = useMemo(() => (scheme.attach?.tables ?? []).flatMap((table) => {
        const tabKey = !scheme.attach.singlePage || table.separatePage ? `attachments-${table.key}` : 'attachments';
        return visibleTabs?.some((tab) => tab.value === tabKey) &&
            visibleAttachmentTables?.some((tab) => tab.value === table.key)
            ? [{ tableKey: table.key, tabKey }] : [];
    }), [scheme.attach, visibleTabs, visibleAttachmentTables]);
    // неизменяемый документ (предпросмотр шаблона, поддержки) за вложениями не следит: ни хаба, ни attachment-state
    const attachmentChanges = useDocumentAttachmentChanges(docId,
        !isNew && !hideAttachments && !isDocumentRuntimeReadOnly(runtime), attachmentLocations);
    const attachmentRequest = attachmentChanges.context.request;
    const tabsWithChanges = useMemo(() => visibleTabs?.map((tab) => {
        const changed = attachmentLocations.some((location) => location.tabKey === tab.value &&
            attachmentChanges.unread.some((change) => change.item.tableKeys.includes(location.tableKey)));
        return changed ? { ...tab, label: <>{tab.label}<span className="attachment-changes-unread" aria-label="Есть изменения файлов">•</span></> } : tab;
    }), [visibleTabs, attachmentLocations, attachmentChanges.unread]);
    const activeTabRequest = useMemo(() => {
        if (attachmentRequest) return { kind: 'attachment' as const, value: attachmentRequest.tabKey,
            nonce: attachmentRequest.id, fieldIds: [], anchorIds: [] };
        if (navigationRequest && (isEdit || isNew) && visibleTabs?.some((tab) => tab.value === navigationRequest.target.tabId))
            return { kind: 'navigation' as const, value: navigationRequest.target.tabId,
                nonce: navigationRequest.nonce, fieldIds: [navigationRequest.target.fieldIndex],
                anchorIds: navigationRequest.target.anchorId ? [navigationRequest.target.anchorId] : [] };
        const invalid = resolveFirstInvalidTabRequest(visibleTabs, errorTabRequest, isEdit || isNew);
        return invalid ? { ...invalid, kind: 'error' as const } : undefined;
    }, [errorTabRequest, isEdit, isNew, visibleTabs, navigationRequest, attachmentRequest]);
    // Each channel owns its counter; encode the kind for Tabs equality without changing the callback nonce.
    const activeTabNonce = activeTabRequest ? activeTabRequest.nonce * 3 +
        ({ error: 0, navigation: 1, attachment: 2 }[activeTabRequest.kind]) : undefined;
    useEffect(() => {
        if (attachmentRequest && !attachmentLocations.some((location) => location.tabKey === attachmentRequest.tabKey &&
            location.tableKey === attachmentRequest.tableKey)) attachmentChanges.context.handled(attachmentRequest.id);
    }, [attachmentRequest, attachmentLocations, attachmentChanges.context]);
    useEffect(() => {
        // недосчитанный набор (вкладки с сетевыми правилами ещё не добавлены) недоступность не доказывает
        if (!navigationRequest || !visibleTabs || !tabsSettled) return;
        if ((isEdit || isNew) && visibleTabs.some((tab) => tab.value === navigationRequest.target.tabId)) return;
        onNavigationUnreachable?.(navigationRequest.nonce);
    }, [navigationRequest, visibleTabs, tabsSettled, isEdit, isNew, onNavigationUnreachable]);
    const handleActiveTabReady = useCallback(
        (value: string, nonce: number | undefined, panel: HTMLElement | null) => {
            if (activeTabRequest?.value !== value || activeTabNonce !== nonce || !panel) return;
            cancelScrollRetry();
            if (activeTabRequest.kind === 'attachment') return;
            if (navigationRequest && activeTabRequest.kind === 'navigation') {
                onNavigationReady?.(navigationRequest, panel);
                return;
            }
            const { fieldIds, anchorIds, nonce: requestNonce } = activeTabRequest;
            const deadline = performance.now() + 3000;
            const attempt = () => {
                scrollRetryRef.current = null;
                if (scrollFirstDocumentFieldIntoView(fieldIds, panel, anchorIds) || performance.now() > deadline) {
                    onErrorTabRequestHandled?.(requestNonce);
                    return;
                }
                scrollRetryRef.current = requestAnimationFrame(attempt);
            };
            attempt();
        },
        [activeTabRequest, activeTabNonce, onErrorTabRequestHandled, cancelScrollRetry, navigationRequest, onNavigationReady],
    );

    return visibleTabs ? (
        <DocumentAttachmentChangesContext value={attachmentChanges.context}>
        <DocumentTabEditModeContext value={isEdit}>
            <Tabs
                className={clsx(
                    'document-tabs',
                    'document-tabs--primary',
                    isEdit || isNew ? 'document-tabs--edit' : 'document-tabs--view',
                    // Видимость вкладок ещё считается для нового режима — смена режима ждёт её.
                    !(tabsSettled && attachmentsSettled) && DOCUMENT_TABS_SETTLING_CLASS,
                )}
                size="sm"
                list={tabsWithChanges ?? visibleTabs}
                navigationAriaLabel="Разделы документа"
                activeValue={activeTabRequest?.value}
                activeValueNonce={activeTabNonce}
                onActiveValueReady={handleActiveTabReady}
                renderInactiveTabs
            />
        </DocumentTabEditModeContext>
        </DocumentAttachmentChangesContext>
    ) : (
        // до первого расчёта видимых вкладок форма пуста — держим скелетон, а не пустое место
        <DocumentSkeleton variant="body" />
    );
};

/**
 * Мемо-граница между карточкой и формой. Document перерисовывается на каждое движение состояния,
 * которое к форме отношения не имеет (formState.isDirty после setValue из каскада, статус
 * автосохранения, состояние кнопок шапки), и без границы каждый такой рендер протаскивал всё
 * дерево вкладок целиком — сотни полей за проход. Пропсы приходят одним мемоизированным объектом
 * (`tabBuilderProps` в Document), так что поверхностного сравнения достаточно: набор его
 * зависимостей и есть полный список причин перерисовать форму.
 */
export default memo(DocumentTabBuilder);
